import React, { useEffect, useRef, useState, useCallback } from 'react'
import PageHeader from '../utils/PageHeader'
import { API_URL } from '../utils/Constants/URLS'
import PageLoader from '../utils/PageLoader'

export default function InvoiceReport() {
  const token = localStorage.getItem('token')
  const _now = new Date()
  const _year = _now.getFullYear()
  const _month = String(_now.getMonth() + 1).padStart(2, '0')
  const defaultStart = `${_year}-${_month}-01`
  const defaultEnd = _now.toISOString().slice(0, 10) // YYYY-MM-DD

  const [data, setData] = useState([])
  const [summary, setSummary] = useState(null)
  const [startDate, setStartDate] = useState(defaultStart)
  const [endDate, setEndDate] = useState(defaultEnd)
  const [loading, setLoading] = useState(false)
  const [pageLoading, setPageLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isDownloading, setIsDownloading] = useState(false);

  const [page, setPage] = useState(1)
  const limit = 10
  const [hasMore, setHasMore] = useState(true)

  const observerRef = useRef(null)

  const buildUrl = (p = page) => {
    const s = encodeURIComponent(startDate)
    const e = encodeURIComponent(endDate)
    return `${API_URL}invoice/report/generate?startDate=${s}&endDate=${e}&page=${p}&limit=${limit}`
  }

  const fetchPage = useCallback(
    async (p = 1, replace = false) => {
      try {
        if (p === 1 && replace) setLoading(true)
        else setPageLoading(true)
        setError(null)

        const res = await fetch(buildUrl(p), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!res.ok) throw new Error(`Server responded with ${res.status}`)
        const result = await res.json()

        const docs = result?.data?.reportData || []
        const paginationInfo = result?.data?.pagination || {}
        const summaryInfo = result?.data?.summary || null

        setData(prev => (p === 1 && replace ? docs : [...prev, ...docs]))
        if (p === 1 && replace) setSummary(summaryInfo)

        const hasNextPage =
          paginationInfo.hasNextPage !== undefined
            ? paginationInfo.hasNextPage
            : docs.length >= limit
        setHasMore(hasNextPage)
      } catch (err) {
        setError(err.message || 'Failed to fetch')
      } finally {
        setLoading(false)
        setPageLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, startDate, endDate]
  )

  useEffect(() => {
    setPage(1)
    fetchPage(1, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, startDate, endDate])

  useEffect(() => {
    if (!hasMore) return
    const sentinel = observerRef.current
    if (!sentinel) return

    const io = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !pageLoading && !loading) {
            setPage(prev => prev + 1)
          }
        })
      },
      { root: null, rootMargin: '200px', threshold: 0.1 }
    )

    io.observe(sentinel)
    return () => io.disconnect()
  }, [hasMore, pageLoading, loading])

  useEffect(() => {
    if (page === 1) return
    fetchPage(page)
  }, [page, fetchPage])

  const handleFetchClick = () => {
    if (startDate > endDate) {
      setError('Start date cannot be after end date')
      return
    }
    setError(null)
    setPage(1)
    fetchPage(1, true)
  }

  const handleReset = () => {
    setStartDate(defaultStart)
    setEndDate(defaultEnd)
    setError(null)
    setPage(1)
    fetchPage(1, true)
  }

  // ─── CSV helpers ───────────────────────────────────────────────────────────
  const quote = value => {
    if (value === null || value === undefined) return '""'
    const str = String(value)
    return `"${str.replace(/"/g, '""')}"`
  }

  const fmtDate = val =>
    val
      ? new Date(val).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
      : ''

  const csvHeaderLabels = [
    'Invoice No',
    'Invoice Date',
    'Invoice Type',
    'Status',
    'Executive Region',
    'Executive Name',
    'Store Name',
    'Buyer PO No',
    'Buyer PO Date',
    'Buyer Name',
    'Buyer GSTIN',
    'Bill to address',
    'Consignee Name',
    'Consignee GSTIN',
    'Ship to address',
    'SO ID',
    'Line item name',
    'Business Line',
    'Product Line',
    'HSN Code',
    'Part Code',
    'List Price',
    'Discount%',
    'Qty',
    'Rate',
    'Total',
    'UOM',
    'GST %',
    'CGST Value',
    'SGST Value',
    'IGST Value',
    'Net Total',
    'Despatch Doc No',
    'Despatch through',
    'Vehicle Details',
    'Other Reference',
    'Committed Delivery Date',
    'Lead Time'
  ]

  const downloadCsv = async () => {
    // 1. Prevent multiple clicks and validate data existence
    if (isDownloading) return;

    try {
      setIsDownloading(true);
      setError(null);

      // 2. Build URL for the FULL dataset
      // Note: We remove 'page' and 'limit' or set a flag like 'fetchAll=true' 
      // depending on your backend API capability.
      const s = encodeURIComponent(startDate);
      const e = encodeURIComponent(endDate);
      const fullUrl = `${API_URL}invoice/report/generate?startDate=${s}&endDate=${e}&pagination=false`;

      const res = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error(`Server responded with ${res.status}`);

      const result = await res.json();
      const allReportData = result?.data?.reportData || [];

      if (allReportData.length === 0) {
        setError('No data found for the selected date range.');
        return;
      }

      // 3. Prepare CSV Headers
      const headerRow = csvHeaderLabels.map(h => quote(h)).join(',');

      // 4. Map ALL data to CSV rows
      const csvLines = allReportData.map(dt => {
        const rowObj = {
          'Invoice No': dt['Invoice No'] ?? dt.invoiceNumber ?? '',
          'Invoice Date': fmtDate(dt['Invoice Date'] ?? dt.invoiceDate),
          'Invoice Type': dt['Invoice Type'] ?? '',
          'Status': dt['Status'] ?? dt.status ?? '',
          'Executive Region': Array.isArray(dt['Executive Region'])
            ? dt['Executive Region'].join(', ')
            : dt['Executive Region'] ?? '',
          'Executive Name': dt['Executive Name'] ?? '',
          'Store Name': dt['Store Name'] ?? '',
          'Buyer PO No': dt['Buyer PO No'] ?? '',
          'Buyer PO Date': fmtDate(dt['Buyer PO Date']),
          'Buyer Name': dt['Buyer Name'] ?? '',
          'Buyer GSTIN': dt['Buyer GSTIN'] ?? '',
          'Bill to address': dt['Bill to address'] ?? '',
          'Consignee Name': dt['Consignee Name'] ?? '',
          'Consignee GSTIN': dt['ConsignSE GSTIN'] ?? '',
          'Ship to address': dt['Ship to address'] ?? '',
          'SO ID': dt['SO ID'] ?? '',
          'Line item name': dt['Line item name'] ?? '',
          'Business Line': dt['Business Line'] ?? '',
          'Product Line': dt['Product Line'] ?? '',
          'HSN Code': dt['HSN Code'] ?? '',
          'Part Code': dt['Part Code'] ?? '',
          'List Price': dt['List Price'] ?? '',
          'Discount%': dt['Discount%'] ?? '',
          Qty: dt['Qty'] ?? '',
          Rate: dt['Rate'] ?? '',
          Total: dt['Total'] ?? '',
          UOM: dt['UOM'] ?? '',
          'GST %': dt['GST %'] ?? '',
          'CGST Value': dt['CGST Value'] ?? '',
          'SGST Value': dt['SGST Value'] ?? '',
          'IGST Value': dt['IGST Value'] ?? '',
          'Net Total': dt['Net Total'] ?? '',
          'Despatch Doc No': dt['Despatch Doc No'] ?? '',
          'Despatch through': dt['Despatch through'] ?? '',
          'Vehicle Details': dt['Vehicle Details'] ?? '',
          'Other Reference': dt['Other Reference'] ?? '',
          'Committed Delivery Date': fmtDate(dt['Committed Delivery Date']),
          'Lead Time': dt['Lead Time'] ?? ''
        };
        // Ensure the order matches the header labels
        return csvHeaderLabels.map(h => quote(rowObj[h])).join(',');
      });

      // 5. Generate and Trigger Download
      const csvContent = [headerRow, ...csvLines].join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_report_${startDate}_to_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Download Error:', err);
      setError(err.message || 'Failed to download the complete report.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <PageHeader
        pageName='INVOICE REPORT'
        breadcrump1='Reports'
        breadcrump2='Invoice Report'
        link='/invoice-report'
      />

      <div className='p-6 min-h-screen'>
        {/* Summary banner */}
        {summary && (
          <div className='max-w-[1400px] mx-2 mb-4 bg-white border border-gray-200 p-4 rounded-md'>
            <div className='flex flex-wrap gap-6 text-sm text-gray-700'>
              <div>
                <span className='font-semibold'>Total Invoices:</span>{' '}
                {summary.totalInvoices}
              </div>
              <div>
                <span className='font-semibold'>Total Line Items:</span>{' '}
                {summary.totalLineItems}
              </div>
              <div>
                <span className='font-semibold'>Total Taxable Value:</span>{' '}
                ₹{Math.round(summary.totalTaxableValue)?.toLocaleString('en-IN')}
              </div>
              <div>
                <span className='font-semibold'>Total Invoice Value:</span>{' '}
                ₹{Math.round(summary.totalInvoiceValue)?.toLocaleString('en-IN')}
              </div>
            </div>
          </div>
        )}

        {/* Date filters */}
        <div className='max-w-[1400px] mx-2 mb-4 bg-white border border-gray-200 p-4 rounded-md'>
          <div className='flex flex-col md:flex-row md:items-end md:justify-between gap-3'>
            <div className='flex flex-col md:flex-row md:items-end gap-3'>
              <div className='flex items-center gap-2'>
                <label className='text-sm font-medium'>Start Date</label>
                <input
                  type='date'
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className='px-2 py-1 border rounded'
                  max={endDate}
                />
              </div>

              <div className='flex items-center gap-2'>
                <label className='text-sm font-medium'>End Date</label>
                <input
                  type='date'
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className='px-2 py-1 border rounded'
                  min={startDate}
                />
              </div>

              <div className='flex items-center gap-2 mt-2 md:mt-0'>
                <button
                  onClick={handleFetchClick}
                  className='px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700'
                >
                  Fetch
                </button>
                <button
                  onClick={handleReset}
                  className='px-4 py-2 border rounded hover:bg-gray-50'
                >
                  Reset
                </button>
              </div>
            </div>

            <div className='flex items-center justify-end'>
              <button
                onClick={downloadCsv}
                disabled={isDownloading}
                className={`px-4 py-2 text-white rounded transition-colors ${isDownloading ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
              >
                {isDownloading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </div>
                ) : (
                  'Download CSV'
                )}
              </button>
            </div>
          </div>

          {error && <div className='mt-3 text-sm text-red-600'>{error}</div>}
        </div>

        {/* Table */}
        <div className='overflow-x-auto bg-white border border-gray-200 max-w-[1400px] mx-2 relative'>
          {loading && (
            <div className='absolute inset-0 z-50 flex items-center justify-center bg-white/60'>
              <PageLoader />
            </div>
          )}

          <table className='min-w-full border-collapse table-auto'>
            <thead className='bg-gradient-to-r from-indigo-500 to-indigo-600 text-white text-sm font-semibold uppercase tracking-wide'>
              <tr>
                {[
                  'Invoice No',
                  'Invoice Date',
                  'Invoice Type',
                  'Status',
                  'Executive Region',
                  'Executive Name',
                  'Store Name',
                  'Buyer PO No',
                  'Buyer PO Date',
                  'Buyer Name',
                  'Buyer GSTIN',
                  'Bill to Address',
                  'Consignee Name',
                  'Consignee GSTIN',
                  'Ship to Address',
                  'SO ID',
                  'Line Item Name',
                  'Business Line',
                  'Product Line',
                  'List Price',
                  'Discount %',
                  'Qty',
                  'Rate',
                  'Total',
                  'UOM',
                  'GST %',
                  'CGST Value',
                  'SGST Value',
                  'IGST Value',
                  'Net Total',
                  'Despatch Doc No',
                  'Despatch Through',
                  'Vehicle Details',
                  'Other Reference',
                  'Committed Delivery Date',
                  'Lead Time'
                ].map(col => (
                  <th
                    key={col}
                    className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className='text-sm text-gray-800'>
              {data.map((dt, index) => (
                <tr key={`${dt?.invoiceNumber ?? 'row'}-${index}`}>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Invoice No'] ?? dt.invoiceNumber}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {fmtDate(dt['Invoice Date'] ?? dt.invoiceDate)}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Invoice Type']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Status'] ?? dt.status}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {Array.isArray(dt['Executive Region'])
                      ? dt['Executive Region'].join(', ')
                      : dt['Executive Region']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Executive Name']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Store Name']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Buyer PO No']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {fmtDate(dt['Buyer PO Date'])}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Buyer Name']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Buyer GSTIN']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Bill to address']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Consignee Name']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Consignee GSTIN']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Ship to address']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['SO ID']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Line item name']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Business Line']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Product Line']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['List Price']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Discount%']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Qty']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Rate']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Total']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['UOM']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['GST %']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['CGST Value']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['SGST Value']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['IGST Value']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Net Total']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Despatch Doc No']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Despatch through']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Vehicle Details']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Other Reference']}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {fmtDate(dt['Committed Delivery Date'])}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt['Lead Time']}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Sentinel for lazy-load */}
          <div
            ref={observerRef}
            className='h-8 flex items-center justify-center'
          >
            {pageLoading && <PageLoader />}
            {!hasMore && !loading && (
              <div className='p-4 text-center text-sm text-gray-500'>
                No more records.
              </div>
            )}
          </div>

          {!loading && data.length === 0 && !error && (
            <div className='p-6 text-center text-sm text-gray-600'>
              No records found for selected date range.
            </div>
          )}
        </div>
      </div>
    </>
  )
}
